import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { adminGetUsers, adminDeleteUser } from "../api";

function timeAgo(isoString) {
  if (!isoString) return "";
  const s = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (s < 60)    return "just now";
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  return d < 30 ? `${d}d ago` : new Date(isoString).toLocaleDateString();
}

function StatCard({ label, value, color = "#f2f2f4" }) {
  return (
    <div style={{
      background: "#111113", border: "0.5px solid #252528",
      borderRadius: 12, padding: "18px 20px", flex: 1, minWidth: 120,
    }}>
      <div style={{
        fontSize: 28, fontWeight: 800, color,
        fontFamily: "'Bricolage Grotesque', sans-serif", letterSpacing: "-1px",
        lineHeight: 1,
      }}>{value}</div>
      <div style={{ fontSize: 11, color: "#55555e", marginTop: 4, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {label}
      </div>
    </div>
  );
}

function DeleteUserBtn({ user, onDeleted, token }) {
  const [confirm, setConfirm] = useState(false);
  const [busy,    setBusy]    = useState(false);

  if (user.is_admin) {
    return (
      <span style={{ fontSize: 10, color: "#9b8de8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        admin
      </span>
    );
  }

  if (confirm) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button
          onClick={async () => {
            setBusy(true);
            await adminDeleteUser(user.id, token);
            setBusy(false);
            onDeleted(user.id);
          }}
          disabled={busy}
          style={{
            padding: "3px 10px", borderRadius: 6, border: "none",
            background: "#9a2a2a", color: "#fff", fontSize: 10,
            cursor: busy ? "default" : "pointer",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >{busy ? "…" : "confirm"}</button>
        <button
          onClick={() => setConfirm(false)}
          style={{
            padding: "3px 8px", borderRadius: 6, fontSize: 10,
            border: "0.5px solid #252528", background: "transparent",
            color: "#55555e", cursor: "pointer",
          }}
        >×</button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirm(true)}
      style={{
        background: "transparent", border: "0.5px solid #3a1a1a",
        borderRadius: 6, padding: "3px 10px",
        color: "#7a3a3a", fontSize: 10, cursor: "pointer",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        transition: "all 0.1s",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "#1a0808"; e.currentTarget.style.color = "#cc4444"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#7a3a3a"; }}
    >delete</button>
  );
}

export default function AdminPage() {
  const { currentUser, token, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    if (!isLoggedIn || !currentUser?.is_admin) {
      navigate("/");
      return;
    }
    load();
  }, [isLoggedIn, currentUser]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGetUsers(token);
      if (data.users) setUsers(data.users);
      else setError(data.error || "Failed to load users");
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }

  const filtered = search.trim()
    ? users.filter(u =>
        u.username.toLowerCase().includes(search.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const totalDiscussions = users.reduce((sum, u) => sum + (u.discussion_count || 0), 0);
  const totalRatings     = users.reduce((sum, u) => sum + (u.rating_count || 0), 0);

  return (
    <div style={{ minHeight: "100vh", background: "#09090b" }}>
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "32px 20px" }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "#FF5C35", boxShadow: "0 0 6px #FF5C35",
            }} />
            <span style={{ fontSize: 10, color: "#FF5C35", fontWeight: 600,
              fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "0.08em" }}>
              ADMIN PANEL
            </span>
          </div>
          <h1 style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 28, fontWeight: 800, color: "#f2f2f4",
            letterSpacing: "-0.8px", lineHeight: 1.1,
          }}>Site Management</h1>
          <p style={{ fontSize: 12, color: "#55555e", marginTop: 4,
            fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Logged in as <span style={{ color: "#9898a6" }}>{currentUser?.username}</span>
          </p>
        </div>

        {/* Stats */}
        {!loading && !error && (
          <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
            <StatCard label="total users"       value={users.length}      color="#f2f2f4" />
            <StatCard label="total discussions" value={totalDiscussions}   color="#FF5C35" />
            <StatCard label="total ratings"     value={totalRatings}       color="#9b8de8" />
            <StatCard label="admins"            value={users.filter(u => u.is_admin).length} color="#d4b84a" />
          </div>
        )}

        {/* User list */}
        <div style={{
          background: "#111113", border: "0.5px solid #252528",
          borderRadius: 14, overflow: "hidden",
        }}>
          <div style={{
            padding: "16px 20px", borderBottom: "0.5px solid #1c1c1f",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            flexWrap: "wrap",
          }}>
            <span style={{
              fontSize: 13, fontWeight: 700, color: "#e0e0e0",
              fontFamily: "'Bricolage Grotesque', sans-serif",
            }}>Users</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="search by username or email…"
              style={{
                background: "#0d0d0f", border: "0.5px solid #252528",
                borderRadius: 8, padding: "6px 12px", fontSize: 12,
                color: "#a0a0a8", fontFamily: "'Plus Jakarta Sans', sans-serif",
                outline: "none", width: 220,
              }}
            />
          </div>

          {loading && (
            <div style={{ padding: "40px 20px", textAlign: "center",
              fontSize: 12, color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              loading users…
            </div>
          )}
          {error && (
            <div style={{ padding: "40px 20px", textAlign: "center",
              fontSize: 12, color: "#9a4a4a", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div style={{ padding: "40px 20px", textAlign: "center",
              fontSize: 12, color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              no users found
            </div>
          )}

          {!loading && !error && filtered.map((user, i) => (
            <div
              key={user.id}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "13px 20px",
                borderBottom: i < filtered.length - 1 ? "0.5px solid #161618" : "none",
                flexWrap: "wrap",
              }}
            >
              {/* Avatar placeholder */}
              <div style={{
                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                background: `hsl(${(user.username.charCodeAt(0) * 17) % 360},35%,14%)`,
                color: `hsl(${(user.username.charCodeAt(0) * 17) % 360},65%,58%)`,
                border: `0.5px solid hsl(${(user.username.charCodeAt(0) * 17) % 360},35%,22%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700,
                fontFamily: "'Bricolage Grotesque', sans-serif",
              }}>
                {user.username.slice(0, 2).toUpperCase()}
              </div>

              {/* User info */}
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: "#e0e0e0",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}>{user.username}</span>
                  {user.is_admin && (
                    <span style={{
                      fontSize: 9, padding: "1px 6px", borderRadius: 4,
                      background: "rgba(155,141,232,0.12)", color: "#9b8de8",
                      border: "0.5px solid rgba(155,141,232,0.2)",
                      fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600,
                    }}>ADMIN</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#55555e", marginTop: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {user.email || user.phone || "no contact"} · joined {timeAgo(user.created_at)}
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#6a6a7a",
                fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <span title="discussions">{user.discussion_count} guffs</span>
                <span title="ratings">{user.rating_count} ratings</span>
              </div>

              {/* Delete button */}
              <DeleteUserBtn
                user={user}
                token={token}
                onDeleted={uid => setUsers(prev => prev.filter(u => u.id !== uid))}
              />
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
